import React, { FC, useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { EmptyState } from '../empty_state/EmptyState';
import {
	changeFilterValueToArrayOfIds,
	fetchWithDifferntSearchName,
	nullSafeGet,
	nullSafeGetOrElse,
} from '../../utils/DataAccessUtils';
import PaginatedReduxTableWithHeader from '../common/PaginatedReduxTableWithHeader';
import { REQUESTED_PARTS_CRUD_ACTION_CREATORS } from '../../actions/requested_parts_actions';
import {
	requestedPartsRestCrudThunksForSupplier,
	revertShipmentForSupplier,
	shipmentsCsvReportGeneratorForSupplier,
	updateTrackingNumberForSupplier,
} from '../../thunks/requested_parts_thunks';
import HyperLink, { ENTITY_TYPE } from '../common/HyperLink';
import { Button, Popconfirm, Popover, message } from 'antd';
import ReadyToShipModal from './ReadyToShipModal';
import { STOPLIGHT_COLORS } from '../../utils/DataFormatterUtils';
import { FILTER_CONFIG_NAMES, FILTER_FIELD_TYPE } from '../../utils/DataConstants';
import { partsRestCrudThunksForSupplier } from '../../thunks/parts_thunks';
import PartLocationDisplay from './PartLocationDisplay';
import { DownloadOutlined, EditOutlined } from '@ant-design/icons';
import FeildTechSLManagePermissionChecker from '../common/FeildTechSLManagePermissionChecker';
import { locationsRestCrudThunksForSupplier } from '../../thunks/locations_thunks';
import DateTimeHover from '../date_time_component/DateTime';
import { vendorsRestCrudThunksForSupplier } from '../../thunks/vendors_thunks';
import SupplierRegionsLocationsTreeSelect from '../regions_tree_select/SupplierRegionsLocationsTreeSelect';
import PartsLabelDisplay from '../LabelDisplay/PartsLabelDisplay';

const SHIPMENTS_TC = 'stockLocationShipmentsIndex';
const AF_TC = 'requestedPartsAdvancedFilters';

const StockLocationShippedPage: FC<any> = ({
	match,
	updateFilters,
	clearAndUpdateFilters,
	updateSorters,
	requestedParts,
	fetchRequestedParts,
	parts,
	fetchParts,
	fetchMultipleParts,
	exportCsv,
	updateTrackingNumber,
	locations,
	fetchLocations,
	fetchMultipleLocations,
	revertShipment,
	companyConfig,
	vendors,
	fetchVendors,
	fetchMultipleVendors,
}): React.ReactElement => {
	const stockLocationId = useMemo(() => match.params.id, [match.params.id]);

	const [downloading, setDownloading] = useState(false);
	const [shipmentModalVisible, setShipmentModalVisible] = useState(false);
	const [revertingShipmentId, setRevertingShipmentId] = useState(null);

	const [updatingRecord, setUpdatingRecord] = useState(null);

	const getStateSliceProp = useCallback(
		(propName) => nullSafeGetOrElse(`${SHIPMENTS_TC}.${propName}`, requestedParts, {}),
		[requestedParts]
	);

	const hideShipmentModal = useCallback(() => {
		setShipmentModalVisible(false);
		setUpdatingRecord(null);
	}, []);

	const refreshRequestedParts = useCallback(() => {
		fetchRequestedParts(
			{},
			SHIPMENTS_TC,
			getStateSliceProp('pagination'),
			getStateSliceProp('sorting'),
			getStateSliceProp('filters')
		);
	}, [fetchRequestedParts, getStateSliceProp]);

	const onShipmentSuccess = useCallback(() => {
		refreshRequestedParts();
		hideShipmentModal();
	}, [hideShipmentModal, refreshRequestedParts]);

	const onUpdateShipment = useCallback(
		(record) => () => {
			setUpdatingRecord(record);
			setShipmentModalVisible(true);
		},
		[]
	);

	const onRevertShimpent = useCallback(
		(record) => () => {
			setRevertingShipmentId(nullSafeGet('id', record));
			revertShipment(record)
				.then(() => {
					message.success('Shipment reverted successfully!');
					refreshRequestedParts();
				})
				.catch(message.error)
				.finally(() => setRevertingShipmentId(null));
		},
		[refreshRequestedParts, revertShipment]
	);

	const autoConsumeEnabled = useMemo(
		() => nullSafeGetOrElse('inventoryConfig.autoConsumeShippedItems', companyConfig, false),
		[companyConfig]
	);

	const shipmentsColumns = useMemo(
		() => [
			{
				title: 'Work Order',
				dataIndex: 'workOrder',
				render: (_) => (
					<div className="flex flex-col">
						<HyperLink
							text={`#${_.id}`}
							entityId={_.id}
							entityType={ENTITY_TYPE.WORK_ORDER_PARTS_AND_EQUPIPMENTS}
							backLinkText="Back to Shipments"
						/>
						{_.createdAt ? (
							<div className="mt-2">
								<DateTimeHover
									timestamp={_.createdAt}
									timeZone={nullSafeGet('location.timeZoneId', _)}
								/>
							</div>
						) : null}
					</div>
				),
				key: 'workOrderCreatedAt',
				sorter: true,
			},
			{
				title: 'Ship To',
				dataIndex: ['workOrder', 'location', 'name'],
			},
			{
				title: 'Part Number',
				dataIndex: ['partsPerStockLocation', 'part', 'partNumber'],
				render: (_) => _ || '--',
			},
			{
				title: 'Part Name',
				dataIndex: ['partsPerStockLocation', 'part', 'name'],
				render: (_, record) => {
					return (
						<div className="flex flex-col gap-y-1">
							{_}
							<PartsLabelDisplay labels={nullSafeGetOrElse('part.partLabels', record, [])} />
						</div>
					);
				},
			},
			{
				title: 'Located at',
				dataIndex: 'partsPerStockLocation',
				render: (ppsl) => <PartLocationDisplay ppsl={ppsl} />,
			},
			{
				title: 'Requested Qty',
				dataIndex: ['actualRequestedQuantity'],
				render: (qty, record) => (
					<div className="flex flex-col">
						<div>
							{record.returnedQuantity ? (
								<div className="flex flex-row items-center">
									<div className="text-gray-400 line-through">{qty}</div>
									<div className="ml-2">{qty - record.returnedQuantity}</div>
								</div>
							) : (
								qty
							)}
						</div>
						{!autoConsumeEnabled ? (
							<>
								{record.consumedQuantity ? (
									<div
										style={{ color: STOPLIGHT_COLORS.pureGreen }}
									>{`Consumed : ${record.consumedQuantity}`}</div>
								) : null}
								{record.returnedQuantity ? (
									<div
										style={{ color: STOPLIGHT_COLORS.pureRed }}
									>{`Returned : ${record.returnedQuantity}`}</div>
								) : null}
							</>
						) : null}
					</div>
				),
			},
			{
				title: 'Requested At',
				dataIndex: 'createdAt',
				render: (_) =>
					_ ? <DateTimeHover timestamp={_} showDate={true} showTime={false} /> : '--',
				key: 'createdAt',
				sorter: true,
			},
			{
				title: 'Shipped At',
				dataIndex: 'markShippedAt',
				render: (_, record) =>
					_ ? (
						<DateTimeHover
							timestamp={_}
							showDate={true}
							showTime={false}
							timeZone={nullSafeGet('workOrder.location.timeZoneId', record)}
						/>
					) : (
						'--'
					),
				key: 'markShippedAt',
				sorter: true,
			},
			{
				dataIndex: 'shipped',
				title: 'Tracking No.',
				render: (_, record) => (
					<div className="flex flex-row items-center">
						<div>{`${nullSafeGetOrElse('trackingNumber', record, 'NA')}`}</div>
						<FeildTechSLManagePermissionChecker>
							<Button type="link" icon={<EditOutlined onClick={onUpdateShipment(record)} />} />
						</FeildTechSLManagePermissionChecker>
					</div>
				),
			},
			{
				render: (_, record) => {
					const revertDisabledMessage =
						nullSafeGet('consumedQuantity', record) > 0
							? 'Revert cannot be done on consumed parts!'
							: nullSafeGet('returnedQuantity', record) > 0
							? 'Revert cannot be done on returned parts!'
							: null;
					return (
						<FeildTechSLManagePermissionChecker>
							<Popconfirm
								onConfirm={onRevertShimpent(record)}
								placement="bottomLeft"
								title="Are you sure you want to revert shipment?"
								okText="Yes"
								cancelText="No"
							>
								<Popover trigger="hover" content={revertDisabledMessage}>
									<Button
										disabled={!!revertDisabledMessage}
										loading={revertingShipmentId === nullSafeGet('id', record)}
										type="ghost"
									>
										Revert
									</Button>
								</Popover>
							</Popconfirm>
						</FeildTechSLManagePermissionChecker>
					);
				},
			},
		],
		[autoConsumeEnabled, onRevertShimpent, onUpdateShipment, revertingShipmentId]
	);

	const filterConfig = useMemo(
		() => [
			{
				label: 'Ship To',
				fieldName: 'shipToLocationIds',
				stateSlice: locations,
				mode: 'multiple',
				filtersValueAccessor: (filtersStateSlice) => {
					const locationIds = nullSafeGet(`${AF_TC}.filters.locationIds`, filtersStateSlice);
					const finalIds = nullSafeGetOrElse(
						`${AF_TC}.filters.regionIds`,
						filtersStateSlice,
						locationIds
					);
					return changeFilterValueToArrayOfIds(finalIds);
				},
				keyAccessor: (loc) => loc.id,
				valueAccessor: (loc) => loc.id,
				handleChange: (ids) => {
					updateFilters(
						{
							locationIds: ids.join(',') || undefined,
							regionIds: undefined,
						},
						AF_TC
					);
				},
				targetCollectionName: AF_TC,
				fetch: fetchLocations,
				fetchMultiple: fetchMultipleLocations,
				renderItem: (loc) => nullSafeGet('name', loc),
				sortBy: { sort_by: 'name', order: 'ascend' },
				component: SupplierRegionsLocationsTreeSelect,
			},
			{
				label: 'Part',
				fieldName: 'partIds',
				mode: 'multiple',
				stateSlice: parts,
				targetCollectionName: AF_TC,
				fetch: fetchWithDifferntSearchName(fetchParts, 'search'),
				fetchMultiple: fetchMultipleParts,
				renderItem: (part) => (
					<div className="flex flex-row items-center">
						<div>{part.name}</div>
						<div>
							{part.partNumber && (
								<span className="text-gray-400">&nbsp;{`- #${part.partNumber}`}</span>
							)}
						</div>
					</div>
				),
			},
			{
				label: 'Default Vendor',
				fieldName: 'defaultVendorIds',
				mode: 'multiple',
				stateSlice: vendors,
				targetCollectionName: AF_TC,
				fetch: fetchVendors,
				fetchMultiple: fetchMultipleVendors,
			},
			{
				label: 'Shipped Between',
				labelPrefix: 'Shipped',
				fieldName: FILTER_CONFIG_NAMES.SHIPPED_AT,
				type: FILTER_FIELD_TYPE.DATE_RANGE,
			},
			{
				fieldName: 'isNotConsumed',
				type: FILTER_FIELD_TYPE.RADIO_GROUP,
				items: [
					{
						label: 'All',
						value: 'undefined',
					},
					{
						label: 'Not Consumed',
						value: 'true',
					},
				],
			},
		],
		[
			locations,
			fetchLocations,
			fetchMultipleLocations,
			parts,
			fetchParts,
			fetchMultipleParts,
			vendors,
			fetchVendors,
			fetchMultipleVendors,
			updateFilters,
		]
	);

	const onUpdateShipmentSubmit = useCallback(
		(values) => updateTrackingNumber({ ...updatingRecord }, values),
		[updateTrackingNumber, updatingRecord]
	);

	const downloadCSV = useCallback(() => {
		setDownloading(true);
		exportCsv({
			...getStateSliceProp('sorting'),
			...getStateSliceProp('filters'),
		}).finally(() => setDownloading(false));
	}, [exportCsv, getStateSliceProp]);

	return (
		<>
			<PaginatedReduxTableWithHeader
				targetCollectionName={SHIPMENTS_TC}
				updateFilters={updateFilters}
				stateSlice={requestedParts}
				clearAndUpdateFilters={clearAndUpdateFilters}
				updateSorters={updateSorters}
				filterConfig={filterConfig}
				entityCollectionName="requested_parts"
				tableColumns={shipmentsColumns}
				showHeader
				preAppliedFilters={{ stockLocationId, shipped: true }}
				initialSorters={{ sort_by: 'markShippedAt', order: 'desc' }}
				fetchData={requestedPartsRestCrudThunksForSupplier.read}
				hasDefaultHeaderPage
				rightActions={
					<Popover content="Export CSV" trigger="hover">
						<Button
							loading={downloading}
							size="large"
							icon={<DownloadOutlined />}
							className="inline-block-visible-md"
							onClick={downloadCSV}
						>
							<span className="inline-block-visible-xxl">Export CSV</span>
						</Button>
					</Popover>
				}
				emptyState={
					<EmptyState
						graphic={
							<img
								style={{ marginBottom: 8 }}
								src="https://s3.amazonaws.com/mock-data-assets/categories/images/cactus.svg"
								alt="No shipments found!"
							/>
						}
						headline={'No shipments found!'}
						body={
							<div style={{ textAlign: 'center' }}>
								<div style={{ maxWidth: 440, marginBottom: 16 }}>
									No shipments found for this stock location
								</div>
							</div>
						}
					/>
				}
			/>
			{shipmentModalVisible && (
				<ReadyToShipModal
					onCancel={hideShipmentModal}
					successCallback={onShipmentSuccess}
					onSubmitShipment={onUpdateShipmentSubmit}
					requestedPart={updatingRecord}
				/>
			)}
		</>
	);
};

const mapStateToProps = (state) => ({
	requestedParts: state.requested_parts,
	parts: state.parts,
	stockLocation: state.stock_locations.detail,
	currentUser: state.session.currentUser,
	locations: state.locations,
	companyConfig: state.company_config.detail,
	vendors: state.vendors,
});

const mapDispatchToProps = (dispatch) => ({
	updateFilters: (filters, targetCollection) =>
		dispatch(REQUESTED_PARTS_CRUD_ACTION_CREATORS.updateFilters(filters, targetCollection)),
	clearAndUpdateFilters: (filters, targetCollectionName) =>
		dispatch(
			REQUESTED_PARTS_CRUD_ACTION_CREATORS.clearAndUpdateFilters(filters, targetCollectionName)
		),
	updateSorters: (sortBy, order, targetCollection) =>
		dispatch(REQUESTED_PARTS_CRUD_ACTION_CREATORS.updateSorting(sortBy, order, targetCollection)),
	fetchRequestedParts: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			requestedPartsRestCrudThunksForSupplier.read(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	fetchMultipleParts: (ids, targetCollectionName) =>
		dispatch(partsRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)),
	fetchParts: (params, targetCollectionName, pagination, sorting, filters, addToTargetCollection) =>
		dispatch(
			partsRestCrudThunksForSupplier.readLite(
				{ ...(params || {}), isActive: true },
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	exportCsv: (params) => dispatch(shipmentsCsvReportGeneratorForSupplier(params)),
	updateTrackingNumber: (entity, data) => dispatch(updateTrackingNumberForSupplier(entity, data)),
	fetchMultipleLocations: (ids, targetCollectionName) =>
		dispatch(locationsRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)),
	fetchLocations: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			locationsRestCrudThunksForSupplier.readLite(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	revertShipment: (entity) => dispatch(revertShipmentForSupplier(entity)),
	fetchVendors: (params, targetCollectionName) =>
		dispatch(
			vendorsRestCrudThunksForSupplier.readLite(
				{ ...params, isActive: true, no_pagination: true },
				targetCollectionName
			)
		),
	fetchMultipleVendors: (ids, targetCollectionName) =>
		dispatch(vendorsRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(StockLocationShippedPage));
